import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { Modal, Button, Field, Input } from "@/components/ui-kit";
import api from "@/lib/api";

const blankVendor = { name: "", contact: "", phone: "", email: "", address: "", npwp: "", bank: "", note: "", is_active: true };

/**
 * Iter v34ct (#2) — Kelola Master Vendor (tambah / edit / hapus) langsung dari
 * halaman Rekap Pengeluaran. Perm: `vendors.manage` atau `expense.vendors_manage`.
 */
export default function VendorManagerModal({ open, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(blankVendor);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/vendors")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Gagal memuat daftar vendor"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) { load(); setForm(blankVendor); setEditId(null); } }, [open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama vendor wajib diisi"); return; }
    setSaving(true);
    try {
      if (editId) await api.patch(`/vendors/${editId}`, form);
      else await api.post("/vendors", form);
      toast.success(editId ? "Vendor diperbarui" : "Vendor ditambahkan");
      setForm(blankVendor); setEditId(null); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan vendor");
    } finally { setSaving(false); }
  };

  const del = async (v) => {
    if (!window.confirm(`Hapus vendor "${v.name}"? Data pengeluaran lama tetap tersimpan.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor dihapus"); load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus vendor");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Kelola Master Vendor" size="lg">
      <div className="space-y-4">
        <div className="border border-stone-200 p-3 bg-stone-50/50 space-y-3" data-testid="vendor-manager-form">
          <div className="flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-widest font-semibold text-stone-600">
              {editId ? "Edit Vendor" : "Tambah Vendor Baru"}
            </p>
            {editId && (
              <button onClick={() => { setEditId(null); setForm(blankVendor); }} className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1" data-testid="vendor-manager-cancel-edit">
                <X className="h-3 w-3" /> Batal edit
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Nama Vendor" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-manager-name" />
            </Field>
            <Field label="Kontak / PIC">
              <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="vendor-manager-contact" />
            </Field>
            <Field label="Telepon">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-manager-phone" />
            </Field>
            <Field label="Email">
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-manager-email" />
            </Field>
            <Field label="NPWP">
              <Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} className="font-mono" />
            </Field>
            <Field label="Rekening Bank">
              <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="BCA 1234567890 a/n ..." />
            </Field>
          </div>
          <Field label="Alamat">
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="vendor-manager-save">
              <Plus className="h-3.5 w-3.5" />{saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Tambah Vendor"}
            </Button>
          </div>
        </div>

        <div className="border border-stone-200 max-h-[300px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] uppercase tracking-wider font-semibold text-stone-600">
                <th className="py-2 px-3">Nama</th>
                <th className="py-2 px-3">Kontak</th>
                <th className="py-2 px-3">Telepon</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-8 text-center text-stone-400">Memuat…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-stone-400">Belum ada vendor.</td></tr>}
              {!loading && rows.map((v, i) => (
                <tr key={v.id} className="border-b border-stone-100" data-testid={`vendor-manager-row-${i}`}>
                  <td className="py-2 px-3 font-medium">{v.name}</td>
                  <td className="py-2 px-3 text-stone-600 text-xs">{v.contact || "—"}</td>
                  <td className="py-2 px-3 font-mono text-xs">{v.phone || "—"}</td>
                  <td className="py-2 px-3 text-xs">{v.email || "—"}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap">
                    <button onClick={() => { setEditId(v.id); setForm({ ...blankVendor, ...v }); }} className="text-stone-500 hover:text-terra-700 px-2" title="Edit" data-testid={`vendor-manager-edit-${i}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => del(v)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`vendor-manager-delete-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end pt-2 border-t border-stone-100">
          <Button variant="ghost" onClick={onClose} data-testid="vendor-manager-close">Tutup</Button>
        </div>
      </div>
    </Modal>
  );
}
